import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, map, catchError, of } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * ActivationGuard - Guides users through the complete registration flow
 * Applied to dashboard routes to ensure users complete all required onboarding steps
 *
 * FLOW ORDER (matches AuthFlowService):
 * 1. Email Verification → /auth/email-verification
 * 2. Choose Plan → /auth/choose-plan (if hasSubscriptions = false)
 * 3. Account Setup → /auth/account-setup (if no social accounts)
 * 4. Choose Instagram → /auth/choose-instagram (if social account fields incomplete)
 * 5. Dashboard → /dashboard (when all required steps completed)
 *
 * LOGIC:
 * - Check email verification first
 * - Check subscription BEFORE social accounts (important!)
 * - Check social accounts exist
 * - Check social account has all required fields (child_business_manager, ad_account, instagram_account)
 * - If all required steps completed → allow access to dashboard
 * 
 * NOTE: pending_activation status does NOT block dashboard access
 * Users can access dashboard while waiting for account activation
 */
@Injectable({
  providedIn: 'root',
})
export class ActivationGuard {
  constructor(private authService: AuthService, private router: Router) {}
  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // If user is not authenticated, let other guards handle it
    if (!this.authService.isAuthenticated()) {
      return true;
    }

    // Try to use cached user profile first to avoid infinite API calls
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        return this.checkActivationFlow(userData);
      } catch (error) {
        console.error('Activation Guard - Error parsing cached user:', error);
      }
    }

    // If no cached user or parsing failed, fetch from API
    return this.authService.getUserProfile().pipe(
      map((profile: any) => {
        const userData = profile?.data || profile;
        return this.checkActivationFlow(userData);
      }),
      catchError((error) => {
        console.error('Error checking activation status:', error);
        return of(true);
      })
    );
  }

  private checkActivationFlow(userData: any): boolean | UrlTree {
    // Follow the same flow order as AuthFlowService
    // Step 1: Check if email is not verified
    if (!userData.email_verified_at) {
      return this.router.createUrlTree(['/auth/email-verification'], {
        queryParams: { email: userData.email }
      });
    }

    // Step 2: Check if user has subscription (MUST CHECK BEFORE SOCIAL ACCOUNTS)
    if (!userData?.hasSubscriptions) {
      return this.router.createUrlTree(['/auth/choose-plan']);
    }

    // Step 3: Check if account setup is not completed (no social accounts)
    if (!userData.socialAccounts || userData.socialAccounts.length === 0) {
      return this.router.createUrlTree(['/auth/account-setup']);
    }

    // Step 4: Check if user has selected Instagram account AND required social account fields
    const facebookProvider = userData.socialAccounts.find(
      (e: any) => e.provider === 'facebook'
    );
    
    // Check if social account has all required fields
    if (facebookProvider) {
      const hasChildBusinessManager = facebookProvider.child_business_manager && 
                                     facebookProvider.child_business_manager !== null &&
                                     !(typeof facebookProvider.child_business_manager === 'object' && 
                                       Object.keys(facebookProvider.child_business_manager).length === 0);
      const hasChildBusinessManagerAdAccount = facebookProvider.child_business_manager_ad_account && 
                                               facebookProvider.child_business_manager_ad_account !== null &&
                                               !(typeof facebookProvider.child_business_manager_ad_account === 'object' && 
                                                 Object.keys(facebookProvider.child_business_manager_ad_account).length === 0);
      const hasInstagramAccount = facebookProvider.instagram_account && 
                                 facebookProvider.instagram_account !== null &&
                                 !(typeof facebookProvider.instagram_account === 'object' && 
                                   Object.keys(facebookProvider.instagram_account).length === 0);
      
      if (!hasChildBusinessManager || !hasChildBusinessManagerAdAccount || !hasInstagramAccount) {
        return this.router.createUrlTree(['/auth/choose-instagram']);
      }
    } else {
      return this.router.createUrlTree(['/auth/account-setup']);
    }

    // All required steps completed, allow access to dashboard
    // Note: pending_activation is allowed - users can access dashboard even while waiting for activation
    return true;
  }
}
